import { useEffect, useState } from 'react';
import { keepPreviousData, useQuery } from '@tanstack/react-query';
import { queryKeys } from '../../lib/queryKeys';
import { useRequiredWorkspace } from '../workspace/useWorkspace';
import { fetchSearch } from './api';

const DEBOUNCE_MS = 200;
const RESULT_LIMIT = 8;

/**
 * Debounced global search over the current workspace.
 *
 * The debounce lives here rather than in the caller: every consumer of
 * search (today, the header dropdown; later, possibly a command palette)
 * wants "don't fire on every keystroke" and none of them want to reimplement
 * it. `query` is the raw text as the user is typing it — trimming and the
 * empty-string short-circuit happen inside, so a caller can pass the search
 * box's value straight through with no guard of its own.
 */
export function useSearch(query: string) {
  const { workspace } = useRequiredWorkspace();
  const trimmed = query.trim();
  const [debounced, setDebounced] = useState(trimmed);

  useEffect(() => {
    const timer = setTimeout(() => setDebounced(trimmed), DEBOUNCE_MS);
    return () => clearTimeout(timer);
  }, [trimmed]);

  return useQuery({
    queryKey: queryKeys.search(workspace.id, debounced, RESULT_LIMIT),
    queryFn: ({ signal }) => fetchSearch(workspace.id, debounced, RESULT_LIMIT, signal),
    // The DTO requires a non-empty `q` (search.dto.ts) — an empty box has no
    // tsquery and no trigram to rank against, so there is nothing to ask for.
    enabled: debounced.length > 0,
    // Keep the previous page's rows on screen while the debounce settles and
    // the next request is in flight, rather than flashing empty on every
    // keystroke — the panel would otherwise blink shut and reopen as you type.
    placeholderData: keepPreviousData,
    staleTime: 15_000,
  });
}
